import React, { useState } from 'react';
import Papa from 'papaparse';
import { COUNTRIES, DEFAULT_COUNTRY } from '../utils/countryUtils';
import {
  detectCurrencyFromCSV,
  fetchExchangeRates,
  convertToEUR,
  getCurrencySymbol,
  COUNTRY_TO_CURRENCY,
} from '../config/currencies';
import { getColumn } from '../config/columnMapping';

const FileUpload = ({ onDataLoaded }) => {
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);

  // Modal state for currency confirmation
  const [pendingImport, setPendingImport] = useState(null); // { cleanData, fileName, currency, rates, liveRate }
  const [customRate, setCustomRate] = useState('');

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset input so the same file can be re-uploaded if needed
    event.target.value = '';

    const reader = new FileReader();

    reader.onload = async (e) => {
      const csvText = e.target.result;

      const processedCsvText = csvText
        .replace(/^﻿/, '')
        .replace(/^뮿/, '')
        .replace(/[ ---]/g, '')
        .replace(/[   -​  　]/g, ' ')
        .replace(/[""]/g, '"')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/[ \t]+$/gm, '');

      // Detect currency from CSV content; also trust the selected country
      const detectedCurrency =
        detectCurrencyFromCSV(processedCsvText) ||
        COUNTRY_TO_CURRENCY[selectedCountry] ||
        'EUR';

      Papa.parse(processedCsvText, {
        header: true,
        skipEmptyLines: true,
        complete: async function (results) {
          if (results.errors.length > 0) {
            console.error('Erreurs de parsing:', results.errors);
            return;
          }

          // Fetch live rates (needed even for EUR in case mixed later)
          const rates = await fetchExchangeRates();
          const liveRate = rates[detectedCurrency] || 1;

          /**
           * Parse a monetary string from any supported locale:
           *   FR/BE  "81,60 €"       → 81.60
           *   PL     "PLN 26.40"     → 26.40
           *   UK     "£15.99"        → 15.99
           *   SE     "SEK 150.00"    → 150.00
           *   CZK    "Kč 399.00"     → 399.00
           * Returns the raw numeric value in the original currency (NOT yet in EUR).
           * Conversion to EUR is applied after modal confirmation.
           */
          const parseAmount = (valueStr) => {
            if (valueStr === undefined || valueStr === null || valueStr === '') return 0;
            const str = String(valueStr).trim();

            // Strip known currency prefixes/suffixes and non-numeric except . , space
            const cleaned = str
              .replace(/PLN/gi, '')
              .replace(/SEK/gi, '')
              .replace(/GBP/gi, '')
              .replace(/CZK/gi, '')
              .replace(/Kč/g, '')
              .replace(/£/g, '')
              .replace(/€/g, '')
              .replace(/zł/g, '')
              .replace(/kr/gi, '')
              .trim();

            // Detect decimal format: if last separator is '.', it's dot-decimal (PL/UK/SE)
            // If last separator is ',', it's comma-decimal (FR/BE)
            const lastDot = cleaned.lastIndexOf('.');
            const lastComma = cleaned.lastIndexOf(',');

            let normalized;
            if (lastComma > lastDot) {
              // Comma is the decimal separator (FR/BE format: "1 234,56" or "81,60")
              normalized = cleaned
                .replace(/[\s.]/g, '')   // remove thousands sep (space or dot)
                .replace(',', '.');
            } else {
              // Dot is the decimal separator (PL/UK/SE format: "1,234.56" or "26.40")
              normalized = cleaned
                .replace(/[\s,]/g, '');  // remove thousands sep (space or comma)
            }

            const result = parseFloat(normalized);
            return isNaN(result) ? 0 : result;
          };

          const parseQuantity = (valueStr) => {
            if (valueStr === undefined || valueStr === null || valueStr === '') return 0;
            const str = String(valueStr).trim();
            const matches = str.match(/([0-9\s.,]+)/);
            if (matches && matches[0]) {
              return parseInt(matches[0].replace(/[\s,.]/g, ''), 10) || 0;
            }
            return 0;
          };

          const cleanData = results.data.map(row => {
            const salesValue    = parseAmount(getColumn(row, 'sales'));
            const b2bSales      = parseAmount(getColumn(row, 'salesB2B'));
            const units         = parseQuantity(getColumn(row, 'units'));

            const allKeys = Object.keys(row);
            const ordersKey = allKeys.find(key =>
              (key.includes('Total') && key.includes('articles commandés')) ||
              (key.includes('Total') && key.includes('produits commandés')) ||
              (key.includes('Total') && key.includes('order items')) ||
              (key.includes('Liczba') && key.includes('zamówion'))
            );
            const totalOrders = ordersKey ? parseQuantity(row[ordersKey]) : 0;

            return {
              ...row,
              'Ventes de produits commandés': salesValue,
              'Ventes de produits commandés — B2B': b2bSales,
              'Unités commandées': units,
              'Total de produits commandés': totalOrders,
            };
          });

          if (detectedCurrency !== 'EUR') {
            // Show confirmation modal before proceeding
            setPendingImport({ cleanData, fileName: file.name, currency: detectedCurrency, rates, liveRate });
            setCustomRate(liveRate.toFixed(4));
          } else {
            // EUR: no conversion needed, import directly
            onDataLoaded(cleanData, file.name, selectedCountry);
          }
        },
      });
    };

    reader.readAsText(file, 'UTF-8');
  };

  const handleConfirmImport = () => {
    if (!pendingImport) return;
    const { cleanData, fileName, currency, rates } = pendingImport;
    const rate = parseFloat(customRate) || rates[currency] || 1;

    // Apply EUR conversion to all monetary fields now that rate is confirmed
    const convertedData = cleanData.map(row => ({
      ...row,
      'Ventes de produits commandés':
        convertToEUR(row['Ventes de produits commandés'], currency, { [currency]: rate }),
      'Ventes de produits commandés — B2B':
        convertToEUR(row['Ventes de produits commandés — B2B'], currency, { [currency]: rate }),
    }));

    onDataLoaded(convertedData, fileName, selectedCountry);
    setPendingImport(null);
    setCustomRate('');
  };

  const handleCancelImport = () => {
    setPendingImport(null);
    setCustomRate('');
  };

  const countryInfo = COUNTRIES.find(c => c.code === selectedCountry);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Currency confirmation modal */}
      {pendingImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Fichier détecté : {COUNTRIES.find(c => c.code === selectedCountry)?.flag}{' '}
              {COUNTRIES.find(c => c.code === selectedCountry)?.name} ({pendingImport.currency})
            </h3>
            <p className="text-sm text-gray-500 mb-4">{pendingImport.fileName}</p>

            <div className="bg-blue-50 rounded-md p-4 mb-4">
              <p className="text-sm font-medium text-blue-800 mb-2">
                Taux de change {pendingImport.currency} → EUR
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-700">
                  Taux live BCE : 1 € =
                </span>
                <input
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  value={customRate}
                  onChange={e => setCustomRate(e.target.value)}
                  className="w-24 px-2 py-1 border border-blue-300 rounded text-sm text-center"
                />
                <span className="text-sm text-blue-700">
                  {getCurrencySymbol(pendingImport.currency)}
                </span>
                <span className="text-xs text-blue-500">(modifiable)</span>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-5">
              Tous les montants seront convertis en EUR pour l'affichage.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelImport}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmImport}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                ✅ Confirmer import
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        {/* Country selector */}
        <div className="mb-4 text-center">
          <h3 className="text-lg font-medium text-gray-700 mb-3">Sélectionner le pays du rapport</h3>
          <div className="flex justify-center">
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Upload zone */}
        <div className="text-center pt-4 border-t border-gray-200">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Importer un fichier CSV pour {countryInfo?.name}
          </label>
          <p className="text-sm text-gray-500 mt-2">
            Les données seront associées au pays sélectionné : {countryInfo?.flag} {countryInfo?.name}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
